import React, { useEffect, useState } from 'react';
import { connectionsAPI } from '../../services/api';

const describeError = (err, fallback) => err?.response?.data?.error || err?.message || fallback;
const intervalLabel = (minutes) => {
  if (minutes === 1440) return 'Daily';
  if (minutes >= 60) return `Every ${minutes / 60} hour${minutes === 60 ? '' : 's'}`;
  return `Every ${minutes} minutes`;
};

/** A list of mapping rules, with a form to add one. */
function MappingRules({ title, hint, sourceLabel, keyName, choices, value, groups, onChange }) {
  const [source, setSource] = useState('');
  const [target, setTarget] = useState(groups[0]);
  const labelOf = (id) => choices.find((c) => c.id === id)?.label || id;
  const add = () => {
    if (!source) return;
    onChange([...value.filter((r) => r[keyName] !== source), { [keyName]: source, to: target }]);
    setSource('');
  };
  return (
    <fieldset className="connection-field">
      <legend>{title}</legend>
      <p className="source-hint">{hint}</p>
      {value.length > 0 && (
        <table className="datasets-table connection-forms">
          <thead><tr><th>{sourceLabel}</th><th>Counts as</th><th aria-label="Actions" /></tr></thead>
          <tbody>
            {value.map((r) => (
              <tr key={r[keyName]}>
                <td>{labelOf(r[keyName])}</td>
                <td>{r.to}</td>
                <td>
                  <button type="button" className="btn-secondary btn-small" aria-label={`Remove ${labelOf(r[keyName])}`} onClick={() => onChange(value.filter((x) => x[keyName] !== r[keyName]))}>Remove</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="custom-field-add">
        <label className="connection-field">
          <span>{sourceLabel}</span>
          <select value={source} onChange={(e) => setSource(e.target.value)}>
            <option value="">Choose</option>
            {choices.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </label>
        <label className="connection-field">
          <span>{`${sourceLabel} counts as`}</span>
          <select value={target} onChange={(e) => setTarget(e.target.value)}>
            {groups.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
        </label>
        <button type="button" className="btn-secondary" disabled={!source} onClick={add}>{`Add ${sourceLabel.toLowerCase()} rule`}</button>
      </div>
    </fieldset>
  );
}

/** What a company's Intacct data covers, and how its accounts map to the figures on reports. */
export default function IntacctSettings({ connector, connection, companyId, onSaved }) {
  const [options, setOptions] = useState(null);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    connectionsAPI.options(connector.id, companyId)
      .then((res) => {
        if (cancelled) return;
        const o = res.data;
        const s = connection.definitions || {};
        setOptions(o);
        setDraft({
          interval: connection.syncIntervalMinutes,
          book: s.book,
          entities: s.entities || o.entities.map((e) => e.id),
          budgets: s.budgets || o.budgets.map((b) => b.id),
          historyMonths: s.historyMonths,
          monthlyCallBudget: s.monthlyCallBudget,
          groupRules: s.groupRules || [],
          accountRules: s.accountRules || [],
        });
      })
      .catch((err) => { if (!cancelled) setError(describeError(err, 'Could not read the options from Sage Intacct')); });
    return () => { cancelled = true; };
    // Options are read once when the settings open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connector.id, companyId]);

  if (error && !options) return <div className="error-message" role="alert">{error}</div>;
  if (!options || !draft) return <p className="source-hint">Reading the options from Sage Intacct…</p>;

  const set = (patch) => {
    setSaved(false);
    setDraft((d) => ({ ...d, ...patch }));
  };
  const toggle = (name, id) => set({ [name]: draft[name].includes(id) ? draft[name].filter((v) => v !== id) : [...draft[name], id] });
  const books = [...new Set([draft.book, ...options.books])];

  const save = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      const res = await connectionsAPI.saveSettings(connector.id, companyId, {
        syncIntervalMinutes: draft.interval,
        settings: {
          book: draft.book,
          entities: draft.entities,
          budgets: draft.budgets,
          historyMonths: Number(draft.historyMonths),
          monthlyCallBudget: Number(draft.monthlyCallBudget),
          groupRules: draft.groupRules,
          accountRules: draft.accountRules,
        },
      });
      setSaved(true);
      onSaved(res.data);
    } catch (err) {
      setError(describeError(err, 'Could not save the settings'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="connection-settings" onSubmit={save}>
      <label className="connection-field">
        <span>Sync frequency</span>
        <select value={draft.interval} onChange={(e) => set({ interval: Number(e.target.value) })}>
          {connector.syncIntervals.map((m) => <option key={m} value={m}>{intervalLabel(m)}</option>)}
        </select>
      </label>

      <label className="connection-field">
        <span>Reporting book</span>
        <select value={draft.book} onChange={(e) => set({ book: e.target.value })}>
          {books.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
      </label>

      <fieldset className="connection-field">
        <legend>Entities to include</legend>
        <p className="source-hint">Figures add together the entities ticked here. Use the same entities as your management accounts.</p>
        <div className="connection-checks">
          {options.entities.map((e) => (
            <label key={e.id}>
              <input type="checkbox" aria-label={`Entity ${e.name}`} checked={draft.entities.includes(e.id)} onChange={() => toggle('entities', e.id)} />
              {e.name} ({e.id})
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset className="connection-field">
        <legend>Budgets to include</legend>
        <p className="source-hint">Statements offer each budget ticked here, and open on the last by name.</p>
        <div className="connection-checks">
          {options.budgets.map((b) => (
            <label key={b.id}>
              <input type="checkbox" aria-label={`Budget ${b.id}`} checked={draft.budgets.includes(b.id)} onChange={() => toggle('budgets', b.id)} />
              {b.id}{b.isDefault ? ' (default in Intacct)' : ''}
            </label>
          ))}
        </div>
      </fieldset>

      <label className="connection-field">
        <span>Months of history</span>
        <input type="number" min="1" max="120" step="1" value={draft.historyMonths} onChange={(e) => set({ historyMonths: e.target.value })} />
      </label>

      <label className="connection-field">
        <span>Monthly API call budget</span>
        <input type="number" min="100" max="1000000" step="100" value={draft.monthlyCallBudget} onChange={(e) => set({ monthlyCallBudget: e.target.value })} />
      </label>
      <p className="source-hint">Counts only calls made by the wallboard, not other integrations using your Intacct allowance. A full sync uses about 135 calls for a company of this size.</p>

      <section className="connection-section" aria-labelledby="account-mapping-heading">
        <h4 id="account-mapping-heading">Account mapping</h4>
        <p className="source-hint">
          Accounts are placed by their Intacct account category. Add a rule where your reporting differs. A rule on an account takes priority over a rule on a group. Changing the mapping recalculates past figures on the next full sync.
        </p>
        <MappingRules
          title="Your account groups"
          hint="Apply a rule to every account in one of your Intacct account groups."
          sourceLabel="Account group"
          keyName="group"
          choices={options.accountGroups.map((g) => ({ id: g, label: g }))}
          value={draft.groupRules}
          groups={options.reportingGroups}
          onChange={(groupRules) => set({ groupRules })}
        />
        <MappingRules
          title="Single accounts"
          hint="Apply a rule to one account."
          sourceLabel="Account"
          keyName="account"
          choices={options.accounts.map((a) => ({ id: a.id, label: `${a.id} ${a.name}` }))}
          value={draft.accountRules}
          groups={options.reportingGroups}
          onChange={(accountRules) => set({ accountRules })}
        />
      </section>

      {error && <div className="error-message" role="alert">{error}</div>}
      <div className="connection-actions">
        <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save settings'}</button>
        {saved && <span className="source-hint connection-saved">Saved. A change to what is read starts a full sync.</span>}
      </div>
    </form>
  );
}
